import { ReactElement, useCallback } from 'react';
import cookies from 'js-cookie';
import { cryptoHelper } from '../util/cryptoHelper';
import { Button } from './Button';

// Icon components for OAuth providers
const GoogleIcon = ({ ...props }: React.SVGProps<SVGSVGElement>) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M18.1713 8.36792H17.5001V8.33333H10.0001V11.6667H14.7096C14.0225 13.6071 12.1763 15 10.0001 15C7.23882 15 5.00007 12.7613 5.00007 10C5.00007 7.23875 7.23882 5 10.0001 5C11.2746 5 12.4342 5.48083 13.3171 6.26625L15.6742 3.90917C14.1859 2.52167 12.1951 1.66667 10.0001 1.66667C5.39799 1.66667 1.66674 5.39792 1.66674 10C1.66674 14.6021 5.39799 18.3333 10.0001 18.3333C14.6022 18.3333 18.3334 14.6021 18.3334 10C18.3334 9.44125 18.2759 8.89583 18.1713 8.36792Z"
      fill="#FFC107"
    />
    <path
      d="M2.6275 6.12125L5.36542 8.12917C6.10625 6.29501 7.90042 5 10.0004 5C11.2746 5 12.4342 5.48083 13.3171 6.26625L15.6742 3.90917C14.1858 2.52167 12.195 1.66667 10.0004 1.66667C6.79917 1.66667 4.02334 3.47375 2.6275 6.12125Z"
      fill="#FF3D00"
    />
    <path
      d="M10.0001 18.3333C12.1526 18.3333 14.1092 17.5096 15.5876 16.17L13.0084 13.9875C12.1432 14.6452 11.0865 15.0009 10.0001 15C7.83258 15 5.99133 13.6179 5.29883 11.6892L2.58008 13.7829C3.96133 16.4817 6.76133 18.3333 10.0001 18.3333Z"
      fill="#4CAF50"
    />
    <path
      d="M18.1713 8.36792H17.5001V8.33334H10.0001V11.6667H14.7096C14.3809 12.5902 13.7889 13.3972 13.0067 13.9879L13.0084 13.9871L15.5876 16.1696C15.4042 16.3363 18.3334 14.1667 18.3334 10C18.3334 9.44126 18.2759 8.89584 18.1713 8.36792Z"
      fill="#1976D2"
    />
  </svg>
);

// const AppleIcon = ({ ...props }: React.SVGProps<SVGSVGElement>) => (
//   <svg
//     width="20"
//     height="20"
//     viewBox="0 0 20 20"
//     fill="none"
//     xmlns="http://www.w3.org/2000/svg"
//     {...props}
//   >
//     <path
//       d="M14.444 6.2549C13.2224 7.0098 12.4678 8.30392 12.4678 9.74183C12.4678 11.3595 13.438 12.8333 14.9111 13.4444C14.6237 14.3791 14.1925 15.2418 13.6535 16.0327C12.8631 17.1471 12.0367 18.2974 10.815 18.2974C9.59341 18.2974 9.23411 17.5784 7.79689 17.5784C6.39561 17.5784 5.89258 18.3333 4.74281 18.3333C3.59304 18.3333 2.80257 17.2908 1.90431 15.9967C0.718608 14.1993 0.0359304 12.1144 0 9.92157C0 6.36274 2.29954 4.45752 4.59909 4.45752C5.82072 4.45752 6.82677 5.24837 7.58131 5.24837C8.29992 5.24837 9.44969 4.42157 10.815 4.42157C12.2523 4.38562 13.6176 5.06863 14.444 6.2549ZM10.1683 2.91176C10.7791 2.19281 11.1025 1.29412 11.1384 0.359477C11.1384 0.251634 11.1384 0.107843 11.1025 0C10.0605 0.107843 9.09039 0.611111 8.40771 1.40196C7.79689 2.08497 7.43759 2.94771 7.40166 3.88235C7.40166 3.9902 7.40166 4.09804 7.43759 4.20588C7.50945 4.20588 7.61724 4.24183 7.6891 4.24183C8.65922 4.16993 9.55748 3.66667 10.1683 2.91176Z"
//       fill="white"
//     />
//   </svg>
// );

interface Props {
  authUrl: string;
  appUrl: string;
  cognitoClientId: string;
  // these need to align with the acitons in authoriseWithAuthorisationCode mutation
  action: 'login' | 'register';
}

export const AuthButtons = ({
  authUrl,
  appUrl,
  cognitoClientId,
  action
}: Props): ReactElement => {
  const handleGoogleAuth = useCallback(async () => {
    const nonce = await cryptoHelper.generateNonce();

    const codeVerifier = await cryptoHelper.generateNonce();

    // THIS IS SET BY COGNITO - SETTING A REMINDER HERE FOR WHEN WE INTEGRATE WITH APPLE
    const idProvider = 'Google';

    const state = `${action}:${idProvider}:${nonce}`;

    const code = cryptoHelper.base64URLEncode(
      await cryptoHelper.sha256(codeVerifier)
    );

    cookies.set(`codeVerifier-${state}`, codeVerifier, {
      expires: 1,
      domain: `.${window.location.hostname}`,
    });

    window.location.href = `https://auth.app.${authUrl}/oauth2/authorize?response_type=code&redirect_uri=${appUrl}/${action}&identity_provider=${idProvider}&state=${state}&client_id=${cognitoClientId}&code_challenge_method=S256&code_challenge=${code}&nonce=${nonce}`;
  }, [action, appUrl, authUrl, cognitoClientId]);

  // const handleAppleAuth = () => {
  //   // TODO: Implement Apple OAuth flow - THIS COSTS SO LEAVING FOR NOW
  //   // Example: window.location.href = `${env.appUrl}/auth/apple?redirect=${window.location.origin}`
  //   console.log('Apple OAuth not implemented yet');
  // };

  return (
    <div
      className="flex flex-col gap-3 items-start w-full"
      role="group"
      aria-label="Registration options"
    >
      <Button
        button={{
          onClick: handleGoogleAuth,
          text: 'Continue with Google',
          IconEnd: GoogleIcon,
          type: 'basic',
          size: 'large',
        }}
      />
      {/* <Button
                  onClick={handleAppleAuth}
                  className="bg-grey-t-900 hover:brightness-125 transition flex gap-3 items-center justify-center px-5 py-3 rounded-[2px] w-full cursor-pointer"
                  aria-label="Continue registration with Apple"
                >
                  <span className="font-bold text-[0.9375rem] leading-[1.5rem] text-white">
                    Continue with Apple
                  </span>
                  <AppleIcon aria-hidden="true" />
                </Button> */}
    </div>
  );
};
